<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CreateWorkingHoursModal from './createWorkingHoursModal.vue';

export default{
    components:{
      CreateWorkingHoursModal,
    },
    head() {
			return {
				style: [
					{
						cssText: `
							@keyframes fadeIn {
									from { opacity: 0; }
									to { opacity: 1; }
							}
							
							@keyframes scaleIn {
									from { transform: scale(0.95); opacity: 0; }
									to { transform: scale(1); opacity: 1; }
							}
							
							.animate-fadeIn {
									animation: fadeIn 0.2s ease-in-out;
							}
							
							.animate-scaleIn {
									animation: scaleIn 0.2s ease-out;
							}
						`
					}
				]
			};
    },
    props:{
        scheduleId: {
            type: String,
            default: null,
        },
        selectedSchedule: {
            type: Object,
            required: true,
        }
    },
    watch: {
        async scheduleId(newId){
            this.refreshPage();
        },
    },
    data(){
        return{
            workingHours: [],
            days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            isCreatingWorkingHours: false,
            isEditingWorkingHours: false,
            editWorkingHoursId: null,
            selectedDay: null,
            selectedWH: null,
            selectedDays: [],
            showErrorId: null,
            showTime: { start: null, id: null },
            timeSlots: [],
            timezones: [],
            selectedTimezone: null,
            searchQuery: "",
            confirmDelete: false,
            confirmDeleteId: null,
        }
    },
    computed:{
        groupedWorkingHours() {
            const grouped = {};
            for (let day = 0; day <= 6; day++) {
                grouped[day] = [];
            }
            const sorted = [...this.workingHours].sort((a, b) => {
                return a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time);
            });
            sorted.forEach((entry) => {
                grouped[entry.day_of_week].push({
                    ...entry,
                    start_time: this.formatTime(entry.start_time),
                    end_time: this.formatTime(entry.end_time),
                });
            });

            return grouped;
        },
        filteredDays() {
            return this.days.filter((day) => day !== this.selectedDay); 
        },
        filteredTimezones() {
            const query = this.searchQuery.toLowerCase();
            return Object.fromEntries(
                Object.entries(this.timezones).filter(([timezone]) =>
                    timezone.toLowerCase().includes(query)
                )
            );
        },
        timezoneOptions() {
            return Object.entries(this.timezones).map(([key, iso]) => ({
                key,
                iso
            }));
        }
    },
    methods: {
			...mapActions('appointments', [
				'editSchedule', 
				'fetchWorkingHours', 
				'deleteWorkingHours', 
				'editWorkingHours', 
				'createWorkingHours', 
				'getTimezones'
			]),

			resetCreateWorkingHours() {
				this.isCreatingWorkingHours = false;
				this.isEditingWorkingHours = false;
				this.editWorkingHoursId = null;
			},

			generateTimeSlots() {
				let slots = [];
				let start = 0;
				let end = 24 * 60;

				for (let minutes = start; minutes < end; minutes += 15) {
					let hours = Math.floor(minutes / 60);
					let mins = minutes % 60;
          let ampm = hours >= 12 ? 'PM' : 'AM';
					let formattedHour = hours % 12 || 12;

					slots.push(`${formattedHour}:${String(mins).padStart(2, "0")} ${ampm}`);
				}

				this.timeSlots = slots;
			},

			async handleEditWorkingHours({ id, dayIndex, startTime, endTime }) {
				const workingHours = this.groupedWorkingHours[dayIndex];
				const oldWorkingHours = workingHours.filter(time => time.id !== id);
        if (this.isDuplicate({ oldTime: oldWorkingHours, newTime: { start_time: startTime, end_time: endTime } })) {
					useAlert(this.$t('BRANCHES.VIEW_WORKING_HOURS.HOURS_DUPLICATED'));
					this.showErrorId = id;
					return;
				}

				try {
					const payload = {
						"day_of_week": dayIndex,
						"end_time": endTime,
						"start_time": startTime
					};

					await this.editWorkingHours({ 
						scheduleId: this.scheduleId, 
						workingHoursId: id, 
						payload 
					});

					useAlert(this.$t('BRANCHES.VIEW_WORKING_HOURS.HOURS_UPDATED'));
					this.showErrorId = null;
				} catch (err) {
					if (Array.isArray(err)) {
						err.forEach(error => {
							console.error(error.msg);
							useAlert(error.msg); // TODO: optionally i18n backend errors
						});
					} else {
						console.error(err);
						useAlert(err); // TODO: optionally i18n
					}
				} finally {
					this.refreshPage();
					this.showTime = { start: null, id: null };
				}
			},

			incrementTime(time) {
				const [hour, minute, second] = time.split(":").map(Number);
				let newHour = hour + 1;
				if (newHour === 24) newHour = 0;
				return `${String(newHour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
			},

			async refreshPage() {
				try {
					this.workingHours = await this.fetchWorkingHours(this.scheduleId);
					this.timezones = await this.getTimezones();
					this.selectedTimezone = this.selectedSchedule.timezone;
					this.generateTimeSlots();
				} catch (err) {
					console.error(err);
					useAlert(err); // TODO: optionally i18n
				}
			},

			async handleCreateWorkingHours({ dayIndex, previousEndTime }) {
				const newStartTime = this.incrementTime(this.parseTime(previousEndTime));
				const newEndTime = this.incrementTime(this.parseTime(newStartTime));

				const payload = {
					"day_of_week": dayIndex,
					"end_time": newEndTime,
					"start_time": newStartTime
				};

				try {
					await this.createWorkingHours({
						scheduleId: this.scheduleId,
						payload
					});
				} catch (err) {
					console.error(err);
					for (error in err) {
						useAlert(error); // TODO: optionally i18n
					}
				} finally {
					this.refreshPage();
				}
			},

			async handleCreateNewWorkingHours(dayIndex) {
				const payload = {
					"day_of_week": dayIndex,
					"end_time": "17:00:00",
					"start_time": "09:00:00"
				};

				try {
					await this.createWorkingHours({
						scheduleId: this.scheduleId,
						payload
					});
				} catch (err) {
					console.error(err);
					for (error in err) {
						useAlert(error); // TODO: optionally i18n
					}
				} finally {
					this.refreshPage();
				}
			},

			openDeleteConfirmation(id) {
				this.confirmDelete = true;
				this.confirmDeleteId = id;
			},

			cancelDeleteConfirmation() {
				this.confirmDelete = false;
				this.confirmDeleteId = null;
			},

			async handleDeleteWorkingHours(id) {
				try {
          await this.deleteWorkingHours({ scheduleId: this.scheduleId, workingHoursId: id });
					useAlert(this.$t('BRANCHES.VIEW_WORKING_HOURS.HOURS_DELETED'));
				} catch (err) {
					console.error(err);
					useAlert(err); // TODO: optionally i18n
				} finally {
					this.refreshPage();
					this.confirmDelete = false;
					this.confirmDeleteId = null;
				}
			},

			async handleCopyWorkingHours(hours) {
				try {
					const workingHours = await this.selectedDays.map(selectedDay =>
						this.groupedWorkingHours[this.days.indexOf(selectedDay)]
					);

					workingHours.map(wh => wh.map(slot =>
						this.deleteWorkingHours({ scheduleId: this.scheduleId, workingHoursId: slot.id })
					));

					const promises = this.selectedDays.flatMap(selectedDay =>
						hours.map(hour => {
							const payload = {
								"day_of_week": this.days.indexOf(selectedDay),
								"end_time": this.parseTime(hour.end_time),
								"start_time": this.parseTime(hour.start_time)
							};
							return this.createWorkingHours({ scheduleId: this.scheduleId, payload });
						})
					);

					await Promise.all(promises);
				} catch (err) {
					console.error(err);
					useAlert(err); // TODO: optionally i18n
				} finally {
					this.closeDropdown();
					this.refreshPage();
				}
			},

			isDuplicate({ oldTime, newTime }) {
				return oldTime.some(
					slot => newTime.start_time < slot.end_time && newTime.end_time > slot.start_time
				);
			},

			formatTimezone(timezone, timestamp) {
				const cityRaw = timezone?.split?.("/")[1] || timezone;
				const city = cityRaw?.replace(/_/g, " ") || "";

				if (!timestamp) return city;

				const match = timestamp.match(/([+-]\d{2}:\d{2})$/);
				const offset = match ? match[1] : "+00:00";
				return `${city} (UTC${offset})`;
			},

			formatTime(time) {
				const [hour, minute] = time.split(":");
				const h = parseInt(hour);
        const suffix = h >= 12 ? 'PM' : 'AM';
				const formattedHour = h % 12 || 12;
				return this.$t('BRANCHES.VIEW_WORKING_HOURS.TIME_FORMAT', {
					hour: formattedHour,
					minute: minute,
					period: suffix
				});
			},

			toggleDayDropdown({ day, id }) {
				this.selectedDay = this.days[day];
				this.selectedWH = id;

				this.$nextTick(() => {
					setTimeout(() => {
						const dropdown = this.$refs.dropdownMenu;
						if (dropdown) {
							dropdown.value.scrollIntoView({ behavior: "smooth", block: "center" });
						} else {
							console.error("Dropdown ref is not working!"); // TODO: optionally i18n this
						}
					}, 100);
				});
			},

			closeDropdown() {
				this.selectedDay = null;
				this.selectedWH = null;
				this.selectedDays = [];
			},

			scrollToSelected() {
				this.$nextTick(() => {
					const selectedButton = this.$refs.timeButtons?.find(
						el => el.dataset.value === this.hour.end_time.split(":")[0]
					);
					if (selectedButton) {
						selectedButton.scrollIntoView({ behavior: "smooth", block: "center" });
					}
				});
			},

			parseTime(time) {
				let [timePart, modifier] = time.split(" ");
				let [hours, minutes] = timePart.split(":");

				hours = parseInt(hours, 10);
				if (modifier === "AM" && hours === 12) {
					hours = 0;
				} else if (modifier === "PM" && hours !== 12) {
					hours += 12;
				}

				return `${String(hours).padStart(2, "0")}:${minutes}:00`;
			},

			async updateScheduleTimezone() {
				const payload = {
					"name": this.selectedSchedule.name,
					"timezone": this.selectedTimezone
				};

				try {
					await this.editSchedule({
						scheduleId: this.scheduleId,
						payload
					});
					this.$emit('refreshSelectedSchedule');
					useAlert(this.$t('BRANCHES.VIEW_WORKING_HOURS.TIMEZONE_UPDATED'))
				} catch (err) {
					console.error(err);
					useAlert(err); // TODO: optionally i18n
				}
			}
		},
    async mounted(){
        await this.refreshPage();
    },
    emit: ['refreshSelectedSchedule']
}
</script>

<template>
  <div class="flex flex-col p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">    <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
      {{ $t('BRANCHES.VIEW_WORKING_HOURS.TITLE') }}
    </h2>

    <multiselect 
      v-model="selectedTimezone"
      :options="Object.keys(timezones)"
      :custom-label="(key) => formatTimezone(key, timezones[key])"
      :placeholder="$t('BRANCHES.VIEW_WORKING_HOURS.SELECT_TIMEZONE')"
      :searchable="true"
      :close-on-select="true"
      :show-labels="false"
      :multiple="false"
      class="w-full text-slate-900 dark:text-white dark:bg-slate-700"
      @input="updateScheduleTimezone"
    />

    <div class="flex flex-col gap-2 p-2 flex-1">
      <div v-for="(hours, day) in groupedWorkingHours" :key="day" class="border-b border-slate-300 dark:border-slate-700 last:border-0">
        <h3 class="text-slate-800 dark:text-slate-200 flex items-center gap-2">
          {{ days[day] }}
        </h3>

        <ul v-if="hours.length > 0" class="space-y-4 mt-2">
          <li v-for="(hour, index) in hours" :key="hour.id" class="flex flex-wrap items-center gap-3 p-3 sm:p-4">
            <!-- Time Inputs -->
            <div class="flex flex-grow justify-between items-center gap-2 w-full sm:w-auto">
              <div class="w-full max-w-[200px]">
                <multiselect 
                  v-model="hour.start_time"
                  :options="timeSlots"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :multiple="false"
                  :class="['w-full text-slate-900 dark:text-white dark:bg-slate-700']"
                  :placeholder="$t('BRANCHES.VIEW_WORKING_HOURS.SELECT_START_TIME')"
                  @input="handleEditWorkingHours({ id: hour.id, dayIndex: day, startTime: parseTime(hour.start_time), endTime: parseTime(hour.end_time) })"
                />
              </div>

              <span class="text-slate-700 dark:text-slate-300 px-1 mb-4">–</span>

              <div class="w-full max-w-[200px]">
                <multiselect 
                  v-model="hour.end_time"
                  :options="timeSlots"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :multiple="false"
                  :class="['w-full text-slate-900 dark:text-white dark:bg-slate-700']"
                  :placeholder="$t('BRANCHES.VIEW_WORKING_HOURS.SELECT_END_TIME')"
                  @input="handleEditWorkingHours({ id: hour.id, dayIndex: day, startTime: parseTime(hour.start_time), endTime: parseTime(hour.end_time) })"
                />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="ml-auto flex gap-2 mt-3 sm:mt-0 mb-4">
              <button
                @click="handleCreateWorkingHours({ dayIndex: day, previousEndTime: hours[hours.length - 1].end_time })"
                class="p-2 text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-opacity-50"
                :aria-label="$t('BRANCHES.VIEW_WORKING_HOURS.ADD_TIME_SLOT')"
                :title="$t('BRANCHES.VIEW_WORKING_HOURS.ADD_TIME_SLOT')"
                :class="{ 'invisible': index !== 0 }"
              >
                <fluent-icon icon="add-circle" class="h-5 w-5" />
              </button>

              <!-- Copy Button with Dropdown -->
              <div class="relative" :class="{ 'invisible': index !== 0 }">
                <button 
                  @click="toggleDayDropdown({ day: day, id: hour.id })"
                  class="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-50"
                  :aria-label="$t('BRANCHES.VIEW_WORKING_HOURS.COPY_TO_DAYS')"
                  :title="$t('BRANCHES.VIEW_WORKING_HOURS.COPY_TO_DAYS')"
                >
                  <fluent-icon icon="copy" class="h-5 w-5" />
                </button>

                <div v-if="selectedDay === days[day] && selectedWH === hour.id" 
                  class="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
                  @click="closeDropdown"
                  aria-hidden="true"
                ></div>

                <div v-if="selectedDay === days[day] && selectedWH === hour.id" 
                  ref="dropdownMenu"
                  class="absolute right-0 sm:right-auto sm:left-0 mt-2 w-64 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 shadow-xl rounded-lg p-4 z-50 transition-all duration-200 origin-top-right"
                  role="dialog"
                  aria-labelledby="copy-dialog-title"
                >
                  <h4 id="copy-dialog-title" class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
                    {{ $t('BRANCHES.VIEW_WORKING_HOURS.COPY_TO') }}
                  </h4>

                  <div class="flex flex-col space-y-2 max-h-48 overflow-y-auto mb-2 border border-slate-200 dark:border-slate-600 rounded-lg p-2">
                    <label v-for="(label, index) in filteredDays" :key="index"
                      class="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors cursor-pointer"
                    >
                      <input 
                        type="checkbox" 
                        :value="label" 
                        v-model="selectedDays" 
                        class="w-4 h-4 text-woot-500 focus:ring-woot-500 rounded border-slate-300 dark:border-slate-600 dark:bg-slate-800"
                        :id="`day-${index}`"
                      />
                      <span class="text-slate-800 dark:text-slate-300" :for="`day-${index}`">{{ label }}</span>
                    </label>
                  </div>

                  <div class="flex justify-end gap-2">
                    <button 
                      @click="closeDropdown"
                      class="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-50"
                    >
                      {{ $t('BRANCHES.VIEW_WORKING_HOURS.CANCEL') }}
                    </button>
                    <button 
                      @click="handleCopyWorkingHours(hours)"
                      class="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                      :disabled="selectedDays.length === 0"
                      :class="{ 'opacity-50 cursor-not-allowed': selectedDays.length === 0 }"
                    >
                      {{ $t('BRANCHES.VIEW_WORKING_HOURS.APPLY') }}
                    </button>
                  </div>
                </div>
              </div>
              <button 
                @click="openDeleteConfirmation(hour.id)"
                v-if="!confirmDelete || confirmDeleteId !== hour.id"
                class="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                :aria-label="$t('BRANCHES.VIEW_WORKING_HOURS.DELETE_TIME_SLOT')"
                :title="$t('BRANCHES.VIEW_WORKING_HOURS.DELETE_TIME_SLOT')" 
              >
                <fluent-icon icon="delete" class="h-5 w-5" />
              </button>

              <!-- Delete confirmation inline -->
              <div v-if="confirmDelete && confirmDeleteId === hour.id" class="flex gap-2 animate-fadeIn">
                <button 
                  @click="cancelDeleteConfirmation"
                  class="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  :aria-label="$t('BRANCHES.VIEW_WORKING_HOURS.CANCEL')"
                >
                  <fluent-icon icon="dismiss" class="h-5 w-5" />
                </button>
                <button 
                  @click="handleDeleteWorkingHours(confirmDeleteId)"
                  class="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  :aria-label="$t('BRANCHES.VIEW_WORKING_HOURS.APPLY')"
                >
                  <fluent-icon icon="checkmark" class="h-5 w-5" />
                </button>
              </div>
            </div>

            <!-- Error message -->
            <div v-if="showErrorId === hour.id"
              class="w-full mt-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-2 rounded-lg flex items-center gap-2 animate-fadeIn">
              <fluent-icon icon="warning" class="h-4 w-4 flex-shrink-0" />
              <p class="text-sm">
                {{ $t('BRANCHES.VIEW_WORKING_HOURS.ERROR_OVERLAP') }}
              </p>
            </div>
          </li>
        </ul>

        <div v-else class="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg my-2 border border-dashed border-slate-300 dark:border-slate-700">
          <p class="text-slate-500 dark:text-slate-400 italic">
            {{ $t('BRANCHES.VIEW_WORKING_HOURS.NO_HOURS') }}
          </p>
          
          <button 
            @click="handleCreateNewWorkingHours(day)"
            class="p-2 text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-opacity-50"
            :aria-label="$t('BRANCHES.VIEW_WORKING_HOURS.ADD_HOURS')"
            :title="$t('BRANCHES.VIEW_WORKING_HOURS.ADD_HOURS')"
          >
            <fluent-icon icon="add-circle" class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
